import {
  ThemeEvents,
  CartAddEvent
} from '@theme/events';

class Free15mlOffer {
  #dialogEl = null;
  #gridEl = null;
  #loadingEl = null;
  #productsCache = null;
  #busy = false;
  #previousCart = null;
  #initialized = false;

  // ============================================
  // 60ML VARIANT IDS
  // ============================================

  #sixtyMlVariantIds = new Set([
    '52017840750876',
    '52016257466652',
    '52017948655900',
    '52015290122524',
    '52017896096028',
    '53620845478172',
  ]);

  constructor() {
    this.#dialogEl = document.querySelector(
      '#free-15ml-offer-dialog'
    );

    if (!this.#dialogEl) {
      console.warn(
        '[free-15ml-offer] Popup not found.'
      );
      return;
    }

    this.#gridEl = this.#dialogEl.querySelector(
      '[data-free-15ml-grid]'
    );

    this.#loadingEl = this.#dialogEl.querySelector(
      '[data-free-15ml-loading]'
    );

    // Listen for Shopify theme cart updates
    document.addEventListener(
      ThemeEvents.cartUpdate,
      this.#onCartUpdate
    );

    // Popup product selection
    this.#dialogEl.addEventListener(
      'click',
      this.#onGridClick
    );

    console.log(
      '[free-15ml-offer] Initialized'
    );

    // Get initial cart state
    this.#initializeCart();
  }

  // ============================================
  // CHECK IF VARIANT IS 60ML
  // ============================================

  #isSixtyMlVariant(variantId) {
    return this.#sixtyMlVariantIds.has(
      String(variantId)
    );
  }

  // ============================================
  // INITIALIZE CART
  // ============================================

  async #initializeCart() {
    try {
      const cart = await this.#fetchCart();

      this.#previousCart =
        this.#createCartSnapshot(cart);

      this.#initialized = true;

      console.log(
        '[free-15ml-offer] Initial cart:',
        this.#previousCart
      );

    } catch (error) {
      console.error(
        '[free-15ml-offer] Failed to initialize cart:',
        error
      );
    }
  }

  // ============================================
  // CREATE SIMPLE CART SNAPSHOT
  // ============================================

  #createCartSnapshot(cart) {
    const snapshot = new Map();

    if (!cart?.items) {
      return snapshot;
    }

    cart.items.forEach((item) => {
      snapshot.set(
        String(item.key),
        {
          key: String(item.key),
          variantId: String(item.variant_id),
          quantity: Number(item.quantity),
          productId: String(item.product_id),
          title: item.product_title,
          variantTitle: item.variant_title
        }
      );
    });

    return snapshot;
  }

  // ============================================
  // CART UPDATE
  // ============================================

#onCartUpdate = async (event) => {
  console.log(
    '[free-15ml-offer] Cart update:',
    event.detail?.data
  );

  // Ignore errors
  if (event.detail?.data?.didError) {
    return;
  }

  // Ignore update caused by our own free gift
  if (
    event.detail?.data?.source ===
    'free-15ml-offer'
  ) {
    return;
  }

  try {
    // ============================================
    // INITIALIZE CART IF NEEDED
    // ============================================

    if (!this.#initialized) {
      const cart = await this.#fetchCart();

      this.#previousCart =
        this.#createCartSnapshot(cart);

      this.#initialized = true;

      console.log(
        '[free-15ml-offer] Cart initialized after update.'
      );

      return;
    }

    // ============================================
    // CHECK PREVIOUS CART
    // ============================================

    const previousHad60ml =
      [...this.#previousCart.values()].some(
        (item) =>
          this.#isSixtyMlVariant(
            item.variantId
          )
      );

    /*
     * IMPORTANT:
     *
     * If 60ml was ALREADY in cart before
     * this cart update, never open popup.
     */

    if (previousHad60ml) {
      console.log(
        '[free-15ml-offer] ❌ 60ml already existed in cart → popup blocked.'
      );

      const cart = await this.#fetchCart();

      this.#previousCart =
        this.#createCartSnapshot(cart);

      return;
    }

    // ============================================
    // GET CURRENT CART
    // ============================================

    const cart = await this.#fetchCart();

    console.log(
      '[free-15ml-offer] Cart:',
      cart.items.map((item) => ({
        key: item.key,
        productId: item.product_id,
        variantId: item.variant_id,
        productTitle: item.product_title,
        variantTitle: item.variant_title,
        quantity: item.quantity
      }))
    );

    // ============================================
    // FIND NEWLY ADDED ITEM
    // ============================================

    const newlyAddedItem =
      this.#findNewlyAddedItem(cart);

    // ============================================
    // ALWAYS UPDATE PREVIOUS CART
    // ============================================

    this.#previousCart =
      this.#createCartSnapshot(cart);

    if (!newlyAddedItem) {
      console.log(
        '[free-15ml-offer] Could not determine newly added item.'
      );

      return;
    }

    // ============================================
    // CHECK NEWLY ADDED VARIANT
    // ============================================

    const variantId =
      String(
        newlyAddedItem.variant_id
      );

    const is60ml =
      this.#isSixtyMlVariant(
        variantId
      );

    console.log(
      '[free-15ml-offer] Newly added variant:',
      variantId,
      'Is 60ml:',
      is60ml
    );

    // ============================================
    // OPEN ONLY FOR NEW 60ML
    // ============================================

    if (is60ml) {
      console.log(
        '[free-15ml-offer] ✅ NEW 60ML ADDED → OPENING POPUP'
      );

      await this.#openOffer();
    }

  } catch (error) {
    console.error(
      '[free-15ml-offer] Cart update error:',
      error
    );
  }
};

  // ============================================
  // FIND NEWLY ADDED ITEM
  // ============================================

  #findNewlyAddedItem(cart) {
    if (!cart?.items) {
      return null;
    }

    /*
     * If there was no previous cart,
     * we cannot reliably know which item was added.
     */
    if (!this.#previousCart) {
      return null;
    }

    for (const item of cart.items) {
      const key =
        String(item.key);

      const currentQuantity =
        Number(item.quantity);

      const previousItem =
        this.#previousCart.get(key);

      /*
       * New line item
       */
      if (!previousItem) {
        console.log(
          '[free-15ml-offer] New cart line:',
          item
        );

        return item;
      }

      /*
       * Existing line increased
       */
      if (
        currentQuantity >
        previousItem.quantity
      ) {
        console.log(
          '[free-15ml-offer] Cart quantity increased:',
          {
            variantId:
              item.variant_id,

            oldQuantity:
              previousItem.quantity,

            newQuantity:
              currentQuantity
          }
        );

        return item;
      }
    }

    return null;
  }

  // ============================================
  // OPEN POPUP
  // ============================================

  async #openOffer() {
    if (!this.#dialogEl) {
      return;
    }

    /*
     * Prevent duplicate opening
     */
    const dialog =
      this.#dialogEl.querySelector(
        'dialog'
      );

    if (
      dialog?.open
    ) {
      console.log(
        '[free-15ml-offer] Popup already open.'
      );

      return;
    }

    console.log(
      '[free-15ml-offer] Opening 15ml popup...'
    );

    this.#dialogEl.showDialog?.();

    await this.#renderProducts();
  }

  // ============================================
  // RENDER PRODUCTS
  // ============================================

  async #renderProducts() {
    if (!this.#gridEl) {
      return;
    }

    const content =
      this.#dialogEl.querySelector(
        '.free-15ml-offer__content'
      );

    const collectionHandle =
      content?.dataset.free15mlCollection;

    if (!collectionHandle) {
      console.warn(
        '[free-15ml-offer] No collection selected.'
      );

      this.#gridEl.innerHTML = `
        <p>
          Please select a collection in Theme Customizer.
        </p>
      `;

      return;
    }

    this.#loadingEl?.removeAttribute(
      'hidden'
    );

    try {
      const response =
        await fetch(
          `/collections/${collectionHandle}/products.json?limit=50`
        );

      if (!response.ok) {
        throw new Error(
          `Collection request failed: ${response.status}`
        );
      }

      const data =
        await response.json();

      this.#productsCache =
        data.products || [];

    } catch (error) {
      console.error(
        '[free-15ml-offer] Failed to load collection:',
        error
      );

      this.#productsCache = [];

    } finally {
      this.#loadingEl?.setAttribute(
        'hidden',
        ''
      );
    }

    if (
      !this.#productsCache.length
    ) {
      this.#gridEl.innerHTML = `
        <p>
          No products are currently available.
        </p>
      `;

      return;
    }

    this.#gridEl.innerHTML =
      this.#productsCache
        .map((product) => {
          const variant =
            product.variants?.find(
              (v) => v.available
            ) ||
            product.variants?.[0];

          const image =
            product.featured_image ||
            product.images?.[0] ||
            '';

          const imageUrl =
            typeof image === 'string'
              ? image
              : image?.src || '';

          return `
            <div class="free-15ml-offer__card">

              ${
                imageUrl
                  ? `
                    <img
                      src="${imageUrl}"
                      alt="${this.#escape(
                        product.title
                      )}"
                      width="300"
                      height="300"
                      loading="lazy"
                      class="free-15ml-offer__image"
                    >
                  `
                  : `
                    <div class="free-15ml-offer__no-image">
                      No image
                    </div>
                  `
              }

              <p class="free-15ml-offer__title">
                ${this.#escape(
                  product.title
                )}
              </p>

              <button
                type="button"
                class="button button-secondary free-15ml-offer__select"
                data-variant-id="${
                  variant?.id || ''
                }"
                ${
                  variant?.available
                    ? ''
                    : 'disabled'
                }
              >
                ${
                  variant?.available
                    ? 'Select'
                    : 'Sold out'
                }
              </button>

            </div>
          `;
        })
        .join('');
  }

  // ============================================
  // SELECT FREE 15ML
  // ============================================

  #onGridClick = async (event) => {
    const button =
      event.target.closest(
        '[data-variant-id]'
      );

    if (
      !button ||
      this.#busy ||
      button.disabled
    ) {
      return;
    }

    const variantId =
      button.dataset.variantId;

    if (!variantId) {
      return;
    }

    this.#busy = true;

    const originalText =
      button.textContent;

    button.disabled = true;
    button.textContent = 'Adding…';

    try {
      await this.#addProductToCart(
        variantId
      );

      this.#dialogEl.closeDialog?.();

    } catch (error) {
      console.error(
        '[free-15ml-offer] Failed to add free 15ml:',
        error
      );

      button.disabled = false;
      button.textContent =
        originalText;

    } finally {
      this.#busy = false;
    }
  };

  // ============================================
  // ADD FREE 15ML TO CART
  // ============================================

  async #addProductToCart(variantId) {
    const sections = [
      ...document.querySelectorAll(
        'cart-items-component[data-section-id]'
      ),
    ]
      .map(
        (element) =>
          element.dataset.sectionId
      )
      .filter(Boolean);

    const uniqueSections =
      [
        ...new Set(sections)
      ];

    const response =
      await fetch(
        '/cart/add.js',
        {
          method: 'POST',

          headers: {
            'Content-Type':
              'application/json',

            Accept:
              'application/json',
          },

          body:
            JSON.stringify({
              items: [
                {
                  id: variantId,
                  quantity: 1,
                },
              ],

              sections:
                uniqueSections.join(','),

              sections_url:
                window.location.pathname,
            }),
        }
      );

    if (!response.ok) {
      const result =
        await response
          .json()
          .catch(() => ({}));

      throw new Error(
        result.description ||
          'Could not add product to cart.'
      );
    }

    const result =
      await response.json();

    const cartResponse =
      await fetch('/cart.js');

    if (!cartResponse.ok) {
      throw new Error(
        'Product added, but cart could not be refreshed.'
      );
    }

    const cart =
      await cartResponse.json();

    /*
     * Tell the theme that this update came
     * from our popup so it doesn't reopen itself.
     */
    document.dispatchEvent(
      new CartAddEvent(
        cart,
        'free-15ml-offer',
        {
          source:
            'free-15ml-offer',

          didError:
            false,

          variantId:
            String(variantId),

          itemCount:
            cart.item_count,

          sections:
            result.sections || {},
        }
      )
    );
  }

  // ============================================
  // FETCH CART
  // ============================================

  async #fetchCart() {
    const response =
      await fetch('/cart.js');

    if (!response.ok) {
      throw new Error(
        'Could not fetch cart.'
      );
    }

    return response.json();
  }

  // ============================================
  // ESCAPE HTML
  // ============================================

  #escape(str) {
    const div =
      document.createElement('div');

    div.textContent =
      str ?? '';

    return div.innerHTML;
  }
}

// ============================================
// INITIALIZE ONLY ONCE
// ============================================

if (
  !window.__free15mlOfferInitialized
) {
  window.__free15mlOfferInitialized =
    true;

  new Free15mlOffer();
}